import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  MessageSquareWarning, Plus, Search, Eye, Edit, Phone, 
  Clock, CheckCircle, XCircle, Loader2, Filter, AlertCircle, Wrench
} from 'lucide-react';
import api from '../../utils/api';

const MobileComplaints = () => {
  const { user } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [createServiceDialogOpen, setCreateServiceDialogOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Additional data for service creation
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);

  // Form states
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile_number: '',
    city: '',
    issue_type: '',
    notes: ''
  });
  const [newStatus, setNewStatus] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [serviceReference, setServiceReference] = useState('');
  
  // Service creation form
  const [serviceForm, setServiceForm] = useState({
    customer_id: '',
    service_type: 'Onsite Field Service',
    description: '',
    priority: 'Normal',
    assigned_to: '',
    due_date: ''
  });

  const statuses = ['New', 'On Process', 'Resolved', 'Cancelled'];

  const fetchComplaints = useCallback(async () => {
    try {
      const response = await api.get('/complaints/');
      setComplaints(response.data);
    } catch (error) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIssueTypes = useCallback(async () => {
    try {
      const response = await api.get('/complaints/public/settings');
      setIssueTypes(response.data.issue_types || []);
    } catch (error) {
      console.error('Failed to load issue types');
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error('Failed to load customers');
    }
  }, []);

  const fetchEngineers = useCallback(async () => {
    try {
      // Try service engineers list first
      const response = await api.get('/services/engineers/list');
      setEngineers(response.data);
    } catch (error) {
      console.error('Failed to load engineers from services:', error);
      // Try the assignable services endpoint
      try {
        const assignableRes = await api.get('/employees/assignable/services');
        setEngineers(assignableRes.data);
      } catch (e) {
        console.error('Failed to load engineers from assignable:', e);
      }
    }
  }, []);

  useEffect(() => {
    fetchComplaints();
    fetchIssueTypes();
    fetchCustomers();
    fetchEngineers();
  }, [fetchComplaints, fetchIssueTypes, fetchCustomers, fetchEngineers]);

  const handleCreateComplaint = async () => {
    if (!formData.customer_name || !formData.mobile_number || !formData.city || !formData.issue_type) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Use internal API for authenticated users (tracks created_by)
      await api.post('/complaints/internal', formData);
      toast.success('Complaint registered successfully');
      setCreateDialogOpen(false);
      setFormData({
        customer_name: '',
        mobile_number: '',
        city: '',
        issue_type: '',
        notes: ''
      });
      fetchComplaints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    
    // If changing to "On Process", require service reference
    if (newStatus === 'On Process' && !serviceReference) {
      toast.error('Please create a service or enter a service reference number');
      return;
    }
    
    if (newStatus === 'Cancelled' && (!cancelReason || cancelReason.length < 5)) {
      toast.error('Cancel reason is mandatory (min 5 characters)');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'Cancelled' && cancelReason) {
        payload.cancel_reason = cancelReason;
      }
      if (newStatus === 'On Process' && serviceReference) {
        payload.service_reference = serviceReference;
      }
      await api.put(`/complaints/${selectedComplaint.id}/status`, payload);
      toast.success('Status updated');
      setUpdateStatusDialogOpen(false);
      setCancelReason('');
      setServiceReference('');
      fetchComplaints();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  // Open create service dialog from complaint
  const handleOpenCreateService = () => {
    // Find customer by mobile number
    const matchingCustomer = customers.find(c => 
      c.mobile === selectedComplaint?.mobile_number || 
      c.phone === selectedComplaint?.mobile_number
    );
    
    // Pre-fill service form with complaint data
    setServiceForm({
      customer_id: matchingCustomer?.id || '',
      service_type: 'Onsite Field Service',
      description: `Complaint: ${selectedComplaint?.complaint_number}\n\nCustomer: ${selectedComplaint?.customer_name}\nIssue Type: ${selectedComplaint?.issue_type}\n\nDescription:\n${selectedComplaint?.message || selectedComplaint?.notes || ''}`,
      priority: 'Normal',
      assigned_to: '',
      due_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    
    setCreateServiceDialogOpen(true);
  };

  const handleCreateService = async () => {
    if (!serviceForm.customer_id || !serviceForm.assigned_to) {
      toast.error('Please select customer and assign an engineer');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/services/', serviceForm);
      const serviceNo = response.data?.service_no || response.data?.id;
      setServiceReference(serviceNo);
      toast.success(`Service ${serviceNo} created successfully`);
      setCreateServiceDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'On Process': return 'bg-amber-100 text-amber-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'New': return <AlertCircle className="w-4 h-4" />;
      case 'On Process': return <Clock className="w-4 h-4" />;
      case 'In Progress': return <Clock className="w-4 h-4" />;
      case 'Resolved': return <CheckCircle className="w-4 h-4" />;
      case 'Cancelled': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const filteredComplaints = complaints.filter(complaint => {
    const matchesSearch = 
      complaint.complaint_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.mobile_number?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || complaint.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">Complaints</h1>
        <Button onClick={() => setCreateDialogOpen(true)} size="sm">
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search complaints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant="outline" size="icon" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-4 h-4" />
          </Button>
        </div>
        
        {showFilters && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statuses.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Complaints List */}
      <div className="space-y-3">
        {filteredComplaints.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <MessageSquareWarning className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No complaints found</p>
            </CardContent>
          </Card>
        ) : (
          filteredComplaints.map((complaint) => (
            <Card key={complaint.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-blue-600">{complaint.complaint_number}</p>
                    <p className="text-sm text-gray-600">{complaint.customer_name}</p>
                  </div>
                  <Badge className={getStatusColor(complaint.status)}>
                    {getStatusIcon(complaint.status)}
                    <span className="ml-1">{complaint.status}</span>
                  </Badge>
                </div>
                
                <div className="space-y-1 text-sm text-gray-600 mb-3">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${complaint.mobile_number}`} className="text-blue-600">{complaint.mobile_number}</a>
                  </div>
                  <p><strong>Issue:</strong> {complaint.issue_type}</p>
                  <p><strong>City:</strong> {complaint.city}</p>
                  {complaint.created_by_name && (
                    <p className="text-xs text-gray-500">Created by: {complaint.created_by_name}</p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => { setSelectedComplaint(complaint); setViewDialogOpen(true); }}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  {complaint.status !== 'Resolved' && complaint.status !== 'Cancelled' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { 
                        setSelectedComplaint(complaint); 
                        setNewStatus(complaint.status);
                        setUpdateStatusDialogOpen(true); 
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    asChild
                  >
                    <a href={`tel:${complaint.mobile_number}`}>
                      <Phone className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Complaint Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Complaint</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer Name *</Label>
              <Input 
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label>Mobile Number *</Label>
              <Input 
                type="tel"
                value={formData.mobile_number}
                onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                placeholder="Mobile number"
              />
            </div>
            <div>
              <Label>City *</Label>
              <Input 
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                placeholder="City"
              />
            </div>
            <div>
              <Label>Issue Type *</Label>
              <Select value={formData.issue_type} onValueChange={(v) => setFormData({...formData, issue_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select issue type" />
                </SelectTrigger>
                <SelectContent>
                  {issueTypes.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateComplaint} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Complaint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Complaint Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedComplaint?.complaint_number}</DialogTitle>
          </DialogHeader>
          {selectedComplaint && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p className="font-medium">{selectedComplaint.customer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge className={getStatusColor(selectedComplaint.status)}>{selectedComplaint.status}</Badge>
                </div>
                <div>
                  <p className="text-gray-500">Mobile</p>
                  <p className="font-medium">{selectedComplaint.mobile_number}</p>
                </div>
                <div>
                  <p className="text-gray-500">City</p>
                  <p className="font-medium">{selectedComplaint.city}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Issue Type</p>
                  <p className="font-medium">{selectedComplaint.issue_type}</p>
                </div>
              </div>
              {selectedComplaint.notes && (
                <div>
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-sm">{selectedComplaint.notes}</p>
                </div>
              )}
              {selectedComplaint.created_by_name && (
                <div>
                  <p className="text-gray-500 text-sm">Created By</p>
                  <p className="text-sm font-medium">{selectedComplaint.created_by_name}</p>
                </div>
              )}
              {selectedComplaint.cancel_reason && (
                <div>
                  <p className="text-gray-500 text-sm">Cancel Reason</p>
                  <p className="text-sm text-red-600">{selectedComplaint.cancel_reason}</p>
                </div>
              )}
              {selectedComplaint.service_reference && (
                <div>
                  <p className="text-gray-500 text-sm">Service Reference</p>
                  <p className="text-sm font-medium text-blue-600">{selectedComplaint.service_reference}</p>
                </div>
              )}
              <div className="text-xs text-gray-500">
                Created: {new Date(selectedComplaint.created_at).toLocaleString('en-IN')}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateStatusDialogOpen} onOpenChange={(open) => {
        setUpdateStatusDialogOpen(open);
        if (!open) {
          setServiceReference('');
          setCancelReason('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="On Process">On Process</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* Show service reference field when On Process */}
            {newStatus === 'On Process' && (
              <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800">
                  <Wrench className="w-4 h-4 inline mr-1" /> Service Required
                </p>
                <div>
                  <Label>Service Reference No.</Label>
                  <Input
                    value={serviceReference}
                    onChange={(e) => setServiceReference(e.target.value)}
                    placeholder="Enter existing service no. or create new"
                  />
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={handleOpenCreateService}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-1" /> Create New Service
                </Button>
              </div>
            )}

            {newStatus === 'Cancelled' && (
              <div>
                <Label>Cancellation Reason *</Label>
                <Textarea 
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Reason for cancellation (min 5 characters)..."
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Service Dialog */}
      <Dialog open={createServiceDialogOpen} onOpenChange={setCreateServiceDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Service from Complaint</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Select
                value={serviceForm.customer_id}
                onValueChange={(v) => setServiceForm({ ...serviceForm, customer_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.company_name} - {customer.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Service Type</Label>
              <Select
                value={serviceForm.service_type}
                onValueChange={(v) => setServiceForm({ ...serviceForm, service_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Onsite Field Service">Onsite Field Service</SelectItem>
                  <SelectItem value="Workshop Service">Workshop Service</SelectItem>
                  <SelectItem value="Remote Support">Remote Support</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Assign Engineer *</Label>
              <Select
                value={serviceForm.assigned_to}
                onValueChange={(v) => setServiceForm({ ...serviceForm, assigned_to: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select engineer" />
                </SelectTrigger>
                <SelectContent>
                  {engineers.map(eng => (
                    <SelectItem key={eng.id} value={eng.id}>
                      {eng.name} ({eng.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priority</Label>
              <Select
                value={serviceForm.priority}
                onValueChange={(v) => setServiceForm({ ...serviceForm, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={serviceForm.due_date}
                onChange={(e) => setServiceForm({ ...serviceForm, due_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Issue Description</Label>
              <Textarea
                value={serviceForm.description}
                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateServiceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateService} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileComplaints;
